import { ReactComponent as IconInformation } from "@/assets/svgs/i.svg";
import { ReactComponent as IconClose } from "@/assets/svgs/icon-close.svg";
import IconRequest from "@/assets/svgs/icon-request.svg";
import Button from "@/components/Button/index";
import CardConfigure from "@/componentsDS/Modal/CardConfigure";
import Modal from "@/componentsDS/Modal/Modal";
import Portal from "@/components/Portal";
import Toast from "@/components/Toast";
import { EnrolmentTypes } from "@/constants/use-case/enrolment";
import useModal from "@/hooks/use-modal";
import usePage from "@/hooks/use-page";
import usePersona from "@/hooks/use-personas";
import useRequests from "@/hooks/use-requests";
import useSchemaProperties from "@/hooks/use-schema-properties";
import useToast from "@/hooks/use-toast";
import { useLocation, useNavigate, useParams } from "@/lib/router-dom/index";
import API_DATA_ELEMENTS_REQUEST from "@/services/api/dataElementsEnrolment";
import rem from "@/utils/rem";
import titleCase from "@/utils/title-case";
import { getUseCaseId } from "@/utils/use-case-storage";
import moment from "moment";
import { Fragment, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import PrincipalTableList from "./agent-principal/principal-table";
import Tags from '@/componentsDS/Tags/Tags';
import Breadcrumbs from "@/componentsDS/Breadcrumbs/index";
import { feature } from "@/utils/feature-verifier";
import { featureConstant } from "@/constants/configuration/feature";


const Page = () => {
	/*=================================================
		General
	/================================================*/
	const user = useSelector((state) => state.auth.user);
	const isAgentPrincipalPhase2Enabled = feature(featureConstant.AGENT_PRINCIPAL_PHASE_2)

	const { useCaseId, requestingType } = useParams();
	const useCase = useMemo(() => getUseCaseId(useCaseId), [useCaseId]);

	const [pageTitle, setPageTitle] = useState("Request");

	const { breadcrumbsItems, setBreadcrumbsItems } = usePage({
		title: pageTitle,
		breadcrumbs: [
			{
				label:'',
				link:'/'
			},
			{
				label:'Use Cases',
				link:'/use-cases/enrolment'
			},
			{
				label: useCase?.name,
				link: `/use-cases/${useCaseId}/dashboard`
			},
			{
				label:'Data Requests',
				link: `/use-cases/${useCaseId}/requests-list`
			}
		],
	});


	const { currentState } = useLocation();
	const { goTo } = useNavigate();
	const statuses = {
		approved: "APPROVED",
		rejected: "REJECTED",
		revoked: "REVOKED",
		pending: "PENDING",
	};

	const requestTypes = {
		review: "REVIEW",
		request: "REQUEST",
	};

	const toastType = {
		success: "success",
		error: "error",
	};

	const CONSTANTS = {
		provideOnBehalf: "Request to Provide on Behalf",
		consumeOneBehalf: "Request to Consume on Behalf",
		dataExchange: "Request to Establish Data Exchange Relationship",
		sentTo: "You have sent the following request:",
		receivedBy: "You have received the following request:",
		requesterContributor: "Requester / Data Contributor",
		requesterConsumer: "Requester / Data Consumer",
		dataElement: "Data Element",
		requestSentProvider: "Request Sent To / Data Provider",
		requestSentTo: "Request Sent To",
		requestSentConsumer: "Request Sent To  / Data Consumer",
		requestSentContributor: "Request Sent To  / Data Contributor",
		selectedPitstops: "Selected Providing Pitstop(s)",
		aboutDataElement: "About Data Element",
		mandatoryFields: "Mandatory Data Fields",
		optionalFields: "Optional Data Fields",
		requestHistory: "Request History",
		requestSubmitted: "Request Submitted",
		Request: "Request",
		Approve_Confirmation:
			"Are you sure you want to proceed to approve this request?",
		Reject_Confirmation:
			"Are you sure you want to reject this request? You will not be able to exchange this data element.",
		Rejection_Reason: "Reason for rejection (Optional)",
		Use_Case: "Use Case",
		Your_Personas: "Your Persona(s)",
	};

	const requestData = currentState?.state?.data;

	const [requestType, setRequestType] = useState();
	const [principalDetails, setPrincipalDetails] = useState([]);

	/*=================================================
		Message Type
	/================================================*/

	const messageType =
		requestingType.toUpperCase() === requestTypes.review
			? {
				sender: requestData?.participant?.name,
				receiver: user?.orgName,
			}
			: {
				receiver: requestData?.participant?.name,
				sender: user?.orgName,
			};

	/*=================================================
		API
	/================================================*/

	const { toastList, addToastMessage } = useToast();
	const {
		view,
		error: errorRequest,
	} = useRequests([]);
	const {
		fetch: fetchPersonas,
		items: personaItems,
		error: errorPersona,
	} = usePersona();
	const {
		requiredFields,
		optionalFields,
		getRequiredFields,
		getOptionalFields,
	} = useSchemaProperties();
	const [loading, setLoading] = useState(false)

	const fetchEnrolPersona = {
		useCaseId: requestData?.useCaseId,
		orgId: user?.organizationId,
	};

	useEffect(async () => {
		fetchPersonas(fetchEnrolPersona);
		getRequiredFields(JSON.parse(requestData?.dataElement?.elementSchema));
		getOptionalFields(JSON.parse(requestData?.dataElement?.elementSchema));

		let title = "Request";
		if (requestingType.toUpperCase() === requestTypes.request) {
			title = "My Requests";
		}
		if (requestingType.toUpperCase() === requestTypes.review) {
			title = "Incoming Request";
		}

		if (typeof useCaseId !== "undefined") {
			setBreadcrumbsItems([
				{
					label:'',
					link:'/'
				},
				{
					label:'Use Cases',
					link:'/use-cases/enrolment'
				},
				{
					label: useCase?.name,
					link: `/use-cases/${useCaseId}/dashboard`
				},
				{
					label:'Data Requests',
					link: `/use-cases/${useCaseId}/requests-list`
				},
				{
					label: title,
					link:''
				}
			]);
		} else {
			setBreadcrumbsItems([
				{
					label:'',
					link:'/'
				},
				{
					label:'Use Cases',
					link:'/use-cases/enrolment'
				},
				{
					label:'Data Requests',
					link: `/use-cases/${useCaseId}/requests-list`
				},
				{
					label: title,
					link:''
				}
			]);
		}

		setPageTitle(title);
	}, []);

	useEffect(() => {
		addToastMessage(errorRequest, toastType.error);
	}, [errorRequest]);

	useEffect(() => {
		addToastMessage(errorPersona, toastType.error);
	}, [errorPersona]);

	useEffect(() => {
		let subPrincipal =
			currentState.state.data.participant?.subscriptionPrincipals?.filter(Boolean);
		let checkedUncheckedSubPrincipal = subPrincipal?.map((principal) => {
			if (principal?.status === "REJECTED" || principal?.status === "REVOKED") {
				return {
					...principal,
					id: principal.principalId,
					name: principal.principalName,
					uen: principal.principalUEN,
					$selected: false,
					$disabled: true,
				};
			} else if (principal?.status === "APPROVED") {
				return {
					...principal,
					id: principal.principalId,
					name: principal.principalName,
					uen: principal.principalUEN,
					$selected: true,
					$disabled: true,
				};
			} else if (principal?.status === "PENDING") {
				return {
					...principal,
					id: principal.principalId,
					name: principal.principalName,
					uen: principal.principalUEN,
					$selected: true,
				};
			}
		});
		setPrincipalDetails(checkedUncheckedSubPrincipal);
	}, []);

	/*=================================================
		Message Type
	/================================================*/

	const {
		visible: visibleConfirmation,
		show: showConfirmation,
		hide: hideConfirmation,
	} = useModal();

	/*=================================================
		API Action Functions
	/================================================*/

	async function requestAction() {
		setLoading(true);

		let rejectAllSubPrincipal = principalDetails.map((principal) => {
			if (principal) {
				if (principal.status === statuses.pending) {
					return {
						principalId: principal.principalId,
						status: "REJECTED",
					};
				} else {
					return {
						principalId: principal.principalId,
						status: principal.status,
					};
				}
			}
		});

		let approveSubPrincipal = principalDetails.map((principal) => {
			if (principal) {
				if (principal.$selected === true) {
					return {
						principalId: principal.principalId,
						status: "APPROVED",
					};
				} else {
					return {
						principalId: principal.principalId,
						status: "REJECTED",
					};
				}
			}
		});

		const payload = {
			type: requestData?.type,
			organizationId: requestData?.participant.orgId ?? (isAgentPrincipalPhase2Enabled ? requestData?.organizationId : undefined),
			dataElementId: requestData?.dataElementId,
			useCaseId: requestData?.useCaseId,
		};

		if (requestType === statuses.approved) {
			payload.subscriptionPrincipals = approveSubPrincipal;
		}

		if (requestType === statuses.rejected) {
			payload.subscriptionPrincipals = rejectAllSubPrincipal;
		}

		const [error] = await API_DATA_ELEMENTS_REQUEST.update(
			requestType,
			payload
		);

		if (error) {
			return addToastMessage(error?.message, toastType.error);
		}

		setLoading(false)
		onBack();
	}

	/*=================================================
		Navigate
	/================================================*/

	function onBack() {
		sessionStorage.setItem("acceptedResponse", requestType);
		if (typeof useCaseId !== "undefined") {
			goTo(`/use-cases/${useCaseId}/requests-list`);
		} else {
			goTo(`/dashboard/requests-list`);
		}
	}

	const Row = (props) => {
		const { caption, title, secCaption, secTitle } = props;
		return (
			<div className="container-content-header__cols">
				<div className="container-content-header__col">
					<div className="container-content-header__caption">
						{caption}
					</div>
					<div className="container-content-header__sub-title">
						{title}
					</div>
				</div>
				{secCaption && (
					<div className="container-content-header__col container-content-header__col--2">
						<div className="container-content-header__caption">
							{secCaption}
						</div>
						<div className="container-content-header__sub-title">
							{secTitle}
						</div>
					</div>
				)}
			</div>
		);
	};

	const RowContainer = (props) => {
		const { caption, secCaption } = props;
		return (
			<>
				<div className="container-content-header__row">
					<div className="container-content-header__cols">
						<div className="container-content-header__col">
							<div className="container-content-header__caption">
								{caption}
							</div>
							<div className="container-content-header__sub-title">
								{messageType?.sender}
							</div>
						</div>
					</div>
				</div>
				<div className="container-content-header__row">
					<div className="container-content-header__cols">
						<div className="container-content-header__col">
							<div className="container-content-header__caption">
								{CONSTANTS.dataElement}
							</div>
							<div className="container-content-header__sub-title">
								{requestData?.dataElement?.elementName}
							</div>
						</div>
						<div className="container-content-header__col">
							<div className="container-content-header__caption">
								{secCaption}
							</div>
							<div className="container-content-header__sub-title">
								{messageType?.receiver}
							</div>
						</div>
						<div className="container-content-header__col">
							<div className="container-content-header__caption">
								{CONSTANTS.selectedPitstops}
							</div>
							<div className="container-content-header__sub-title">
								{requestData?.systems?.map((system, index) => {
									return (
										<Fragment key={index}>
											{system.systemName}
										</Fragment>
									);
								})}
							</div>
						</div>
					</div>
				</div>
			</>
		);
	};

	/*=================================================
		Principal Table
	/================================================*/

	const renderPrincipalTable = () => {
		if (!requestData.participant?.subscriptionPrincipals || requestData.participant?.subscriptionPrincipals?.filter(Boolean)?.length === 0) {
			return;
		}

		return (
			<PrincipalTableList
				principals={principalDetails}
				setPrincipals={setPrincipalDetails}
				pagination={false}
				searchPrincipal={undefined}
			/>
		);
	};

	let status = requestData.status;
	if (requestData?.participant?.subscriptionPrincipals?.length) {
		if (requestData?.participant?.subscriptionPrincipals?.some((p) => p?.status === "PENDING")) {
			status = statuses.pending;
		} else if (requestData?.participant?.subscriptionPrincipals?.every((p) => p?.status === statuses.approved)) {
			status = statuses.approved;
		} else if (requestData?.participant?.subscriptionPrincipals?.every((p) => p?.status === statuses.rejected)) {
			status = statuses.rejected;
		} else if (requestData?.participant?.subscriptionPrincipals?.every((p) => p?.status === statuses.revoked)) {
			status = statuses.revoked;
		}
	}

	return (
		<>
			<Breadcrumbs breadcrumbItems={breadcrumbsItems} />

			<div className="title-header">
				<div className="title-header__left">
					<div className="title-header__title">{pageTitle}</div>
				</div>
				<div className="title-header__right"></div>
			</div>

			<div className="request-view">
				<div className="request-view-container">
					<div className="container-header">
						<div className="container-header-avatar">
							<div
								className="container-header-avatar__image"
								style={{
									backgroundImage: `url(${IconRequest})`,
								}}
							></div>
						</div>
						<div className="container-header-content">
							<div className="container-header-content__top">
								{(
									`${requestData?.type}` === EnrolmentTypes.consumer ||
									`${requestData?.type}` === EnrolmentTypes.consumeOnBehalfOfPrincipal
								) && (
										<>{CONSTANTS.dataExchange}</>
									)}
								{(
									`${requestData?.type}` === EnrolmentTypes.provider ||
									`${requestData?.type}` === EnrolmentTypes.provideOnBehalfOfPrincipal
								) && (
										<>{CONSTANTS.dataExchange}</>
									)}
								{`${requestData?.type}` === EnrolmentTypes.provideOnBehalf && (
									<>{CONSTANTS.provideOnBehalf}</>
								)}
								{`${requestData?.type}` === EnrolmentTypes.consumeOnBehalf && (
									<>{CONSTANTS.consumeOneBehalf}</>
								)}
							</div>
							<div className="container-header-content__bottom">
								{moment(requestData?.created).format(
									"DD MMM YYYY, h:mm A"
								)}
							</div>
						</div>
						<div className="container-header-action">
							<div className="data-table__table__body__content__button data-table__table__body__content--flex-end">
								{(
									(status === statuses.pending) &&
									requestingType.toUpperCase() === requestTypes.review
								) && (<>
									<Button
										text="Approve"
										disabled={(principalDetails?.length > 0 && principalDetails?.every(s => s?.$selected === false)) ?? false}
										loading={loading}
										submit={() => {
											setRequestType(
												statuses.approved
											);
											showConfirmation();
										}}
									/>
									<Button
										text="Reject"
										ghost
										loading={loading}
										submit={() => {
											setRequestType(
												statuses.rejected
											);
											showConfirmation();
										}}
									/>
								</>)}
							</div>
						</div>
					</div>
					<div className="container-content">
						<div className="container-content-header">
							<div className="container-content-header__desc">
								{requestingType.toUpperCase() === requestTypes.review && (
									<>{CONSTANTS.receivedBy}</>
								)}
								{requestingType.toUpperCase() === requestTypes.request && (
									<>{CONSTANTS.sentTo}</>
								)}
							</div>

							<div className="container-content-header__desc">
								{requestingType.toUpperCase() ===
									requestTypes.request && (
										<>
											{(`${requestData?.type}` === EnrolmentTypes.consumer ||
												`${requestData?.type}` === EnrolmentTypes.consumeOnBehalfOfPrincipal
											) && (<div
												data-testid="request-api-consumer-layout"
												className="container-content-header__rows"
											>
												<Row
													caption={
														"Requester / Data Consumer"
													}
													title={messageType?.sender}
												/>
												<Row
													caption={
														CONSTANTS.dataElement
													}
													title={
														requestData?.dataElement
															?.elementName
													}
													secCaption={
														"Request Sent To  / Data Contributor"
													}
													secTitle={
														messageType?.receiver
													}
												/>
											</div>
												)}

											{(`${requestData?.type}` === EnrolmentTypes.provider ||
												`${requestData?.type}` === EnrolmentTypes.provideOnBehalfOfPrincipal
											) && (
													<div
														data-testid="request-api-provider-layout"
														className="container-content-header__rows"
													>
														<Row
															caption={
																"Requester / Data Contributor"
															}
															title={messageType?.sender}
														/>
														<Row
															caption={
																CONSTANTS.dataElement
															}
															title={
																requestData?.dataElement
																	?.elementName
															}
															secCaption={
																"Request Sent To / Data Consumer"
															}
															secTitle={
																messageType?.receiver
															}
														/>
													</div>
												)}

											{`${requestData?.type}` ===
												EnrolmentTypes.provideOnBehalf && (
													<div
														data-testid="request-api-provider-onbehalf-layout"
														className="container-content-header__rows"
													>
														<RowContainer
															caption={
																"Requester / Data Contributor"
															}
															subCaption={
																"Request Sent To / Data Provider"
															}
														/>
													</div>
												)}

											{`${requestData?.type}` ===
												EnrolmentTypes.consumeOnBehalf && (
													<div
														data-testid="request-api-consumer-onbehalf-layout"
														className="container-content-header__rows"
													>
														<RowContainer
															caption={
																"Requester / Data Consumer"
															}
															subCaption={
																"Request Sent To"
															}
														/>
													</div>
												)}
										</>
									)}

								{requestingType.toUpperCase() ===
									requestTypes.review && (
										<>
											{`${requestData?.type}` ===
												EnrolmentTypes.consumer && (
													<div className="container-content-header__rows">
														<Row
															caption={
																"Requester / Data Contributor"
															}
															title={messageType?.sender}
														/>
														<Row
															caption={
																CONSTANTS.dataElement
															}
															title={
																requestData?.dataElement
																	?.elementName
															}
															secCaption={
																"Request Sent To  / Data Consumer"
															}
															secTitle={
																messageType?.receiver
															}
														/>
													</div>
												)}

											{`${requestData?.type}` ===
												EnrolmentTypes.provider && (
													<div className="container-content-header__rows">
														<Row
															caption={
																"Requester / Data Consumer"
															}
															title={messageType?.sender}
														/>
														<Row
															caption={
																CONSTANTS.dataElement
															}
															title={
																requestData?.dataElement
																	?.elementName
															}
															secCaption={
																"Request Sent To  / Data Contributor"
															}
															secTitle={
																messageType?.receiver
															}
														/>
													</div>
												)}

											{`${requestData?.type}` ===
												EnrolmentTypes.provideOnBehalf && (
													<div className="container-content-header__rows">
														<RowContainer
															caption={
																"Requester / Data Contributor"
															}
															subCaption={
																"Request Sent To / Data Provider"
															}
														/>
													</div>
												)}

											{`${requestData?.type}` ===
												EnrolmentTypes.consumeOnBehalf && (
													<div className="container-content-header__rows">
														<RowContainer
															caption={
																"Requester / Data Consumer"
															}
															subCaption={
																"Request Sent To"
															}
														/>
													</div>
												)}

											{`${requestData?.type}` ===
												EnrolmentTypes.provideOnBehalfOfPrincipal && isAgentPrincipalPhase2Enabled && (
													<div className="container-content-header__rows" data-testid="review-provide-on-behalf-of-principal-layout">
														<Row
															caption={
																"Requester / Consumer"
															}
															title={messageType?.sender}
														/>
														<Row
															caption={
																CONSTANTS.dataElement
															}
															title={
																requestData?.dataElement
																	?.elementName
															}
															secCaption={
																"Request Sent To  / Data Contributor"
															}
															secTitle={
																messageType?.receiver
															}
														/>
													</div>
												)}

											{`${requestData?.type}` ===
												EnrolmentTypes.consumeOnBehalfOfPrincipal && isAgentPrincipalPhase2Enabled && (
													<div className="container-content-header__rows" data-testid="review-consume-on-behalf-of-principal-layout">
														<Row
															caption={
																"Requester / Contributor"
															}
															title={messageType?.sender}
														/>
														<Row
															caption={
																CONSTANTS.dataElement
															}
															title={
																requestData?.dataElement
																	?.elementName
															}
															secCaption={
																"Request Sent To  / Data Consumer"
															}
															secTitle={
																messageType?.receiver
															}
														/>
													</div>
												)}
										</>
									)}
							</div>
						</div>
						<div className="container-content__item">
							<div className="container-content__item__header">
								{CONSTANTS.aboutDataElement}
							</div>
							<div className="container-content__item__content">
								<div className="container-content-item-left">
									<div className="container-content-item-header">
										{CONSTANTS.mandatoryFields}
									</div>
									<div className="container-content-item__cols">
										{requiredFields?.map((data, index) => {
											return (
												<div
													key={index}
													className="container-content-item__col"
												>
													{data?.name}
												</div>
											);
										})}
									</div>
								</div>
								<div className="container-content-item-right">
									<div className="container-content-item-header">
										{CONSTANTS.optionalFields}
									</div>
									<div className="container-content-item__cols">
										{optionalFields?.map((data, index) => {
											return (
												<div
													key={index}
													className="container-content-item__col"
												>
													{data?.name}
												</div>
											);
										})}
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>

				{/* Side box below */}
				<div className="request-view-container-right">
					<div className="request-view-container-right-box">
						<div className="container-header-use-case">
							<div className="container-header-use-case-title">
								{CONSTANTS.Use_Case}
							</div>
							<div className="container-header-use-case-bottom">
								{requestData?.useCase?.useCaseTitle}
							</div>
						</div>
						<div className="container-header-use-case-content">
							<div className="container-header-use-case-content-title">
								{CONSTANTS.Your_Personas}
							</div>
							<div className="container-header-use-case-tag-group flex-display--withWrap ">
								{personaItems?.map((data) => {
									return (
										<span key={data?.id}>
											<Tags label={data?.name} tagClassName='DS__tag--system'/>
										</span>
									);
								})}
							</div>
						</div>
					</div>
					<div className="request-view-container-right">
						<div className="request-view-container-right-box-bottom">
							{renderPrincipalTable()}
						</div>
					</div>
				</div>
			</div>

			<Modal visible={visibleConfirmation} variant="medium" close={hideConfirmation}>
				<CardConfigure>
					<CardConfigure.Header>
						<CardConfigure.Header.Icon>
							<IconInformation />
						</CardConfigure.Header.Icon>
						<CardConfigure.Header.Title>
							<div className="card-configure__header__left">
								<div className="card-configure__title">
									<div className="card-configure__title__left">

										<div className={'card-configure__icon'}>
											<IconInformation />
										</div>
									</div>
									<div className="card-configure__title__right">
										<div className="card-configure__title__right__top">
											{titleCase(requestType)} Request
										</div>
									</div>
								</div>
							</div>
						</CardConfigure.Header.Title>
					</CardConfigure.Header>
					<CardConfigure.Body>
						<CardConfigure.Body.Col>
							{requestType === statuses.approved && (
								<>
									<div
										style={{
											fontSize: rem(14),
											textAlign: "center",
										}}
									>
										Are you sure you want to proceed to
										approve this request?
									</div>
								</>
							)}
							{requestType === statuses.rejected && (
								<>
									<div
										style={{
											fontSize: rem(14),
											textAlign: "center",
										}}
									>
										Are you sure you want to reject this
										request? You will not be able to
										exchange this data element.
									</div>
								</>
							)}
						</CardConfigure.Body.Col>
					</CardConfigure.Body>
					<CardConfigure.Footer>
						<CardConfigure.Footer.Right>
							<Button
								ghost
								text="Yes"
								submit={() => {
									hideConfirmation();
									requestAction();
								}}
							/>

							<span
								style={{
									width: rem(10),
									display: "inline-block",
								}}
							></span>

							<Button
								text="No"
								submit={() => {
									hideConfirmation();
								}}
							/>
						</CardConfigure.Footer.Right>
					</CardConfigure.Footer>
				</CardConfigure>
			</Modal>

			<Toast toastList={toastList} />
		</>
	);
};
export default Page;
